import { useEffect, useState } from "react";
import { Button, Card, showToast } from "@xpense/ui";
import { api } from "../api/client";
import { Page, Section, EmptyState } from "../components/Page";
import { Icon } from "../components/Icons";

interface GmailAccount {
  id: string;
  email: string;
  sync_status: string;
  last_fetched_at: string | null;
  error_message: string | null;
  created_at: string;
}

export default function ConnectedAccounts() {
  const [accounts, setAccounts] = useState<GmailAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get<{ data: GmailAccount[] }>("/api/v1/gmail/accounts");
      setAccounts(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load accounts");
    } finally {
      setLoading(false);
    }
  };

  const handleLinkGmail = async () => {
    setLinking(true);
    try {
      const redirectUri = `${window.location.origin}/gmail-callback`;
      const { url } = await api.get<{ url: string; state: string }>(
        `/api/v1/gmail/auth-url?redirect_uri=${encodeURIComponent(redirectUri)}`
      );
      window.location.href = url;
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to start Gmail linking", "error");
      setLinking(false);
    }
  };

  const handleUnlink = async (accountId: string) => {
    if (!confirm("Unlink this Gmail account? Email fetching will stop.")) return;

    try {
      await api.delete(`/api/v1/gmail/accounts/${accountId}`);
      setAccounts((prev) => prev.filter((a) => a.id !== accountId));
      showToast("Gmail account unlinked", "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to unlink account", "error");
    }
  };

  const handleToggleSync = async (account: GmailAccount) => {
    const newStatus = account.sync_status === "active" ? "paused" : "active";
    try {
      await api.patch(`/api/v1/gmail/accounts/${account.id}`, { sync_status: newStatus });
      setAccounts((prev) =>
        prev.map((a) => (a.id === account.id ? { ...a, sync_status: newStatus } : a))
      );
      showToast(`Email fetching ${newStatus === "active" ? "resumed" : "paused"}`, "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to update account", "error");
    }
  };

  if (loading) {
    return (
      <Page eyebrow="Integrations" title="Connected Accounts" description="Link external services to automate expense tracking.">
        <div style={{ padding: "var(--xpn-space-xl)" }}>
          <div className="xpn-skeleton xpn-skeleton--title" />
          <div className="xpn-skeleton xpn-skeleton--text" style={{ marginTop: "var(--xpn-space-md)" }} />
        </div>
      </Page>
    );
  }

  return (
    <Page
      eyebrow="Integrations"
      title="Connected Accounts"
      description="Link your financial accounts to automate expense tracking from emails, SMS, and notifications."
    >
      <Section
        title="Gmail Accounts"
        description="Link one or more Gmail accounts to automatically extract financial transactions from bank alerts, credit card statements, investment updates, and more."
        action={
          <Button variant="primary" size="sm" onClick={handleLinkGmail} loading={linking}>
            <Icon name="link" />
            Link Gmail Account
          </Button>
        }
      >
        {error ? (
          <div style={{ padding: "var(--xpn-space-xl)" }}>
            <EmptyState
              icon="warning"
              title="Failed to load accounts"
              description={error}
              tone="danger"
            />
          </div>
        ) : accounts.length === 0 ? (
          <EmptyState
            icon="mail"
            title="No accounts linked"
            description="Connect a Gmail account to start extracting transactions from your emails every 30 minutes."
            action={
              <Button variant="primary" onClick={handleLinkGmail} loading={linking}>
                <Icon name="link" />
                Link Your First Account
              </Button>
            }
          />
        ) : (
          <div className="xpn-row-list">
            {accounts.map((account) => (
              <div key={account.id} className="xpn-account-row">
                <div style={{ display: "flex", gap: "var(--xpn-space-md)", alignItems: "center", minWidth: 0 }}>
                  <div className="xpn-txn-icon">
                    <Icon name="mail" />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className="xpn-txn-title">{account.email}</div>
                    <div className="xpn-txn-meta">
                      <span style={{ color: account.sync_status === "active" ? "var(--xpn-color-success)" : "var(--xpn-color-warning)" }}>
                        {account.sync_status === "active" ? "● Active" : "⏸ Paused"}
                      </span>
                      {account.last_fetched_at && (
                        <span> · Last fetch: {new Date(account.last_fetched_at + "Z").toLocaleString("en-IN")}</span>
                      )}
                      {account.error_message && (
                        <span style={{ color: "var(--xpn-color-danger)" }}> · {account.error_message}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "var(--xpn-space-sm)", alignItems: "center", flexShrink: 0 }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleToggleSync(account)}
                  >
                    {account.sync_status === "active" ? "Pause" : "Resume"}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleUnlink(account.id)}
                  >
                    <Icon name="unlink" />
                    Unlink
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="How it works" description="Financial emails are automatically scanned for transactions.">
        <div className="xpn-section-body" style={{ display: "flex", flexDirection: "column", gap: "var(--xpn-space-md)" }}>
          <div className="xpn-flex" style={{ gap: "var(--xpn-space-md)", alignItems: "flex-start" }}>
            <div className="xpn-badge xpn-badge--soft">1</div>
            <div>
              <div className="xpn-txn-title">Link your Gmail</div>
              <div className="xpn-text-caption xpn-text-muted">
                Authorize read-only access so Xpense AI can scan your inbox for financial emails.
              </div>
            </div>
          </div>
          <div className="xpn-flex" style={{ gap: "var(--xpn-space-md)", alignItems: "flex-start" }}>
            <div className="xpn-badge xpn-badge--soft">2</div>
            <div>
              <div className="xpn-txn-title">Automatic scanning</div>
              <div className="xpn-text-caption xpn-text-muted">
                Every 30 minutes, we check for new emails and identify financial ones using smart filters.
              </div>
            </div>
          </div>
          <div className="xpn-flex" style={{ gap: "var(--xpn-space-md)", alignItems: "flex-start" }}>
            <div className="xpn-badge xpn-badge--soft">3</div>
            <div>
              <div className="xpn-txn-title">AI extraction & classification</div>
              <div className="xpn-text-caption xpn-text-muted">
                Financial emails are parsed using AI to extract amounts, merchants, categories, and payment modes — just like SMS and manual entries.
              </div>
            </div>
          </div>
        </div>
      </Section>
    </Page>
  );
}
